import { Select } from "./ui/select.tsx";
import { KeyIcon, ProhibitIcon } from "@phosphor-icons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { type FormEvent, useState } from "react";
import { api, q } from "./api.ts";
import { day } from "./format.ts";
import { notify } from "./feedback.ts";
import { GRANT_LABELS as L } from "./labels.ts";
import { Button, Empty, ErrorText, Field, Modal, Section, Switch } from "./ui.tsx";
import { ListRow } from "./ui/list.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { Chip } from "./ui/status.tsx";

// A product's pre-authorizations (design/architecture.md §2.6, plan M7 step
// 7d): the person grants kinds of work on named assets, with a weekly limit
// and an expiry; plans fully inside one apply without waiting. The server
// accepts these only from a person's session.

const KINDS: C.CreateGrantInput["kinds"] = ["repo_change", "owned_page", "reply_batch"];
const EXPIRY = [7, 30, 90] as const;

export function Grants({ product }: { product: C.Product }) {
  const client = useQueryClient();
  const grants = useQuery(q.grants(product.id));
  const [revoking, setRevoking] = useState<C.Grant | null>(null);
  const refresh = () => client.invalidateQueries({ queryKey: q.grants(product.id).queryKey });
  const revoke = useMutation({ mutationFn: (id: string) => api.del<C.Grant>(`/api/grants/${encodeURIComponent(id)}`), onSuccess: async () => { setRevoking(null); await refresh(); } });
  // The standing pre-authorization for own assets (design/architecture.md §2.6, 2026-11-01): a product setting, a person's session only.
  const setDefault = useMutation({
    mutationFn: (enabled: boolean) => api.patch<C.Product>(`/api/products/${encodeURIComponent(product.id)}`, { own_assets_preauthorized: enabled }),
    onSuccess: async () => { notify.success(L.defaultSaved); await client.invalidateQueries({ queryKey: ["product", product.id] }); await client.invalidateQueries({ queryKey: q.products.queryKey }); },
  });
  const list = grants.data ?? [];
  return <>
    <Section title={L.defaultTitle} note={L.defaultNote}>
      <div className="z-check-row">
        <Switch checked={product.own_assets_preauthorized} onChange={(enabled) => setDefault.mutate(enabled)} label={L.defaultLabel} />
        <span>{product.own_assets_preauthorized ? L.defaultOn : L.defaultOff}</span>
      </div>
      <ErrorText error={setDefault.error} />
    </Section>
    <p className="z-section-note">{L.note}</p>
    {grants.isLoading ? <Skeleton shape="list" rows={2} /> : list.length ? <div className="z-boxed">
      {list.map((grant) => <ListRow key={grant.id} icon={<KeyIcon size={16} />}
        title={grant.assets.join(", ")}
        sub={grant.note ?? undefined}
        chips={<>
          <Chip variant="soft" tone={grant.status === "active" ? "teal" : "gray"} dot>{L.status[grant.status]}</Chip>
          {grant.kinds.map((kind) => <Chip key={kind}>{L.kind[kind] ?? kind}</Chip>)}
        </>}
        meta={grant.status === "active" ? L.used(grant.used_this_week, grant.max_actions_per_week) : undefined}
        end={grant.status === "active"
          ? <><span className="z-lrow-meta">{L.expires(day(grant.expires_at.slice(0, 10)))}</span><Button size="sm" tone="ghost" icon={<ProhibitIcon />} onClick={() => setRevoking(grant)}>{L.revoke}</Button></>
          : <span className="z-lrow-meta">{L.ended(day((grant.revoked_at ?? grant.expires_at).slice(0, 10)))}</span>} />)}
    </div> : grants.isSuccess && <Empty>{L.empty}</Empty>}
    <ErrorText error={grants.error} />
    <Section title={L.add}><GrantForm productId={product.id} onSaved={refresh} /></Section>
    <Modal open={revoking !== null} onOpenChange={(open) => { if (!open) setRevoking(null); }} title={L.revokeTitle}
      footer={<><Button tone="ghost" onClick={() => setRevoking(null)}>{L.cancel}</Button><Button tone="danger" busy={revoke.isPending} onClick={() => { if (revoking) revoke.mutate(revoking.id); }}>{L.confirmRevoke}</Button></>}>
      <p>{L.revokeNote}</p>
      {revoking && <p className="z-mono">{revoking.assets.join(", ")}</p>}
      <ErrorText error={revoke.error} />
    </Modal>
  </>;
}

function GrantForm({ productId, onSaved }: { productId: string; onSaved: () => Promise<unknown> }) {
  const [kinds, setKinds] = useState<C.CreateGrantInput["kinds"]>(["repo_change"]);
  const [assets, setAssets] = useState("");
  const [perWeek, setPerWeek] = useState(5);
  const [days, setDays] = useState<(typeof EXPIRY)[number]>(30);
  const [note, setNote] = useState("");
  const create = useMutation({
    mutationFn: () => api.post<C.Grant>(`/api/products/${encodeURIComponent(productId)}/grants`, {
      kinds,
      assets: assets.split("\n").map((line) => line.trim()).filter(Boolean),
      max_actions_per_week: perWeek,
      expires_in_days: days,
      note: note.trim() || null,
    } satisfies C.CreateGrantInput),
    onSuccess: async () => { setAssets(""); setNote(""); await onSaved(); },
  });
  const submit = (event: FormEvent) => { event.preventDefault(); create.mutate(); };
  const toggle = (kind: C.CreateGrantInput["kinds"][number]) => setKinds((current) => current.includes(kind) ? current.filter((value) => value !== kind) : [...current, kind]);
  return <form onSubmit={submit} className="z-form z-flow">
    <fieldset className="z-fieldset">
      <legend>{L.kinds}</legend>
      <ul className="z-check">{KINDS.map((kind) => <li key={kind}><input id={`grant-kind-${kind}`} type="checkbox" checked={kinds.includes(kind)} onChange={() => toggle(kind)} /><label htmlFor={`grant-kind-${kind}`}>{L.kind[kind]}</label></li>)}</ul>
    </fieldset>
    <Field label={L.assets} help={L.assetsHelp}><textarea className="z-textarea z-mono" rows={3} value={assets} onChange={(event) => setAssets(event.target.value)} required placeholder="repo:docs/*" /></Field>
    <div className="z-form-grid">
      <Field label={L.perWeek}><input className="z-input" type="number" min={1} max={20} value={perWeek} onChange={(event) => setPerWeek(Number(event.target.value))} required /></Field>
      <Field label={L.expiresIn}><Select variant="form" value={String(days)} onValueChange={(nextValue) => setDays(Number(nextValue) as (typeof EXPIRY)[number])} options={EXPIRY.map((value) => ({ value: String(value), label: L.days(value) }))} /></Field>
    </div>
    <Field label={L.noteLabel}><input className="z-input" value={note} maxLength={300} onChange={(event) => setNote(event.target.value)} /></Field>
    <div><Button type="submit" tone="primary" busy={create.isPending} disabled={!kinds.length}>{L.create}</Button></div>
    <ErrorText error={create.error} />
  </form>;
}
